import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@/lib/i18n';
import { Button, Input, Label, RadioGroup } from '@/components/ui';
import { ApiClientError } from '@/lib/http-core';
import type {
  Funding,
  FundingReward,
  FundingRewardCreateRequest,
  FundingRewardUpdateRequest,
} from '@/lib/api-admin/types';
import {
  FUNDING_REWARD_TYPES,
  emptyFundingRewardValues,
  fundingRewardSchema,
  type FundingRewardValues,
} from '../schemas/fundingFormSchema';
import {
  useCreateFundingRewardMutation,
  useDeleteFundingRewardMutation,
  useUpdateFundingRewardMutation,
} from '../hooks/useFundingMutation';

const errorMessageOf = (err: unknown): string | null => {
  if (!err) return null;
  if (err instanceof ApiClientError) return err.message ?? err.code;
  if (err instanceof Error) return err.message;
  return null;
};

// 라이브 와이어 정본(2026-05-31): settlement reward 는 minPledgeAmount 를 plain number 로
// 받는다(응답은 MoneyDto). MoneyDto 객체로 보내면 역직렬화 실패 → 500. OpenAPI 가 Money 로
// 오선언돼 있어 cast 로 우회.
const toCreateBody = (values: FundingRewardValues): FundingRewardCreateRequest => {
  const body: Record<string, unknown> = {
    tierName: values.tierName,
    type: values.type,
    minPledgeAmount: Number(values.minPledgeAmount),
    sortOrder: values.sortOrder ?? 0,
  };
  if (values.description && values.description.length > 0)
    body.description = values.description;
  if (values.maxQuantity && values.maxQuantity.length > 0)
    body.maxQuantity = Number(values.maxQuantity);
  if (values.estimatedDeliveryAt && values.estimatedDeliveryAt.length > 0)
    body.estimatedDeliveryAt = values.estimatedDeliveryAt;
  return body as unknown as FundingRewardCreateRequest;
};

const toUpdateBody = (values: FundingRewardValues): FundingRewardUpdateRequest => {
  const body: Record<string, unknown> = {
    tierName: values.tierName,
    type: values.type,
    minPledgeAmount: Number(values.minPledgeAmount),
  };
  if (values.description !== undefined) body.description = values.description ?? null;
  if (values.maxQuantity !== undefined) {
    body.maxQuantity = values.maxQuantity ? Number(values.maxQuantity) : null;
  }
  if (values.estimatedDeliveryAt !== undefined)
    body.estimatedDeliveryAt = values.estimatedDeliveryAt || null;
  if (values.sortOrder !== undefined) body.sortOrder = values.sortOrder;
  return body as unknown as FundingRewardUpdateRequest;
};

const toFormValues = (reward: FundingReward): FundingRewardValues => ({
  tierName: reward.tierName,
  description: reward.description ?? '',
  type: reward.type,
  minPledgeAmount: String(reward.minPledgeAmount.amount),
  maxQuantity: reward.maxQuantity != null ? String(reward.maxQuantity) : '',
  estimatedDeliveryAt: reward.estimatedDeliveryAt ?? '',
  sortOrder: reward.sortOrder ?? 0,
});

export interface RewardManagerProps {
  funding: Funding;
  canWrite: boolean;
}

interface RewardFormProps {
  campaignId: string;
  defaultValues: FundingRewardValues;
  isEdit: boolean;
  editTarget?: FundingReward;
  lockedDueToPledges?: boolean;
  onDone: () => void;
}

const RewardForm = ({
  campaignId,
  defaultValues,
  isEdit,
  editTarget,
  lockedDueToPledges,
  onDone,
}: RewardFormProps) => {
  const { t } = useTranslation('funding');
  const createMutation = useCreateFundingRewardMutation();
  const updateMutation = useUpdateFundingRewardMutation();
  const form = useForm<FundingRewardValues>({
    resolver: zodResolver(fundingRewardSchema),
    defaultValues,
    mode: 'onBlur',
  });

  const mutation = isEdit ? updateMutation : createMutation;
  const error = errorMessageOf(mutation.error);

  const fieldError = (name: keyof FundingRewardValues): string | undefined => {
    const e = form.formState.errors[name];
    if (e && typeof e === 'object' && 'message' in e && typeof e.message === 'string') {
      return e.message;
    }
    return undefined;
  };

  const onSubmit = (values: FundingRewardValues) => {
    if (isEdit && editTarget) {
      updateMutation.mutate(
        { campaignId, rewardId: editTarget.rewardId, body: toUpdateBody(values) },
        { onSuccess: () => onDone() },
      );
    } else {
      createMutation.mutate(
        { campaignId, body: toCreateBody(values) },
        { onSuccess: () => onDone() },
      );
    }
  };

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="grid grid-cols-1 gap-3 rounded-lg p-4 md:grid-cols-2"
      style={{
        background: 'var(--surface-1)',
        boxShadow: 'inset 0 0 0 1px var(--content-muted)',
      }}
      noValidate
      data-testid="reward-form"
    >
      <div className="flex flex-col gap-1">
        <Label htmlFor="tierName" required>{t('funding.reward.tierName')}</Label>
        <Input
          id="tierName"
          {...form.register('tierName')}
          placeholder={t('funding.reward.tierNamePlaceholder')}
          maxLength={100}
          required
          aria-required="true"
          aria-invalid={Boolean(fieldError('tierName'))}
          style={{ height: 'var(--density-compact-form-h)' }}
        />
        {fieldError('tierName') ? (
          <span role="alert" className="text-xs" style={{ color: 'var(--signal-danger)' }}>
            {t(fieldError('tierName')!, { defaultValue: fieldError('tierName')! })}
          </span>
        ) : null}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label id="reward-type-label">{t('funding.reward.type')}</Label>
        <Controller
          control={form.control}
          name="type"
          render={({ field }) => (
            <RadioGroup
              value={field.value}
              onValueChange={field.onChange}
              options={FUNDING_REWARD_TYPES.map((opt) => ({
                value: opt,
                label: t(`funding.reward.type_${opt}`),
              }))}
              disabled={lockedDueToPledges ?? false}
              orientation="horizontal"
              aria-labelledby="reward-type-label"
            />
          )}
        />
      </div>

      <div className="md:col-span-2 flex flex-col gap-1">
        <Label htmlFor="description">{t('funding.reward.description')}</Label>
        <textarea
          id="description"
          {...form.register('description')}
          maxLength={2000}
          rows={3}
          className="w-full rounded-md border px-3 py-2 text-sm bg-[color:var(--surface-0)] text-[color:var(--content-strong)] focus-visible:outline-none focus-visible:ring-2"
          style={{ borderColor: 'var(--content-muted)' }}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="minPledgeAmount" required>{t('funding.reward.minPledgeAmount')}</Label>
        <Input
          id="minPledgeAmount"
          {...form.register('minPledgeAmount')}
          inputMode="decimal"
          required
          aria-required="true"
          disabled={lockedDueToPledges}
          aria-invalid={Boolean(fieldError('minPledgeAmount'))}
          style={{ height: 'var(--density-compact-form-h)' }}
        />
        {fieldError('minPledgeAmount') ? (
          <span role="alert" className="text-xs" style={{ color: 'var(--signal-danger)' }}>
            {t(fieldError('minPledgeAmount')!, {
              defaultValue: fieldError('minPledgeAmount')!,
            })}
          </span>
        ) : null}
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="maxQuantity">{t('funding.reward.maxQuantity')}</Label>
        <Input
          id="maxQuantity"
          {...form.register('maxQuantity')}
          inputMode="numeric"
          aria-invalid={Boolean(fieldError('maxQuantity'))}
          style={{ height: 'var(--density-compact-form-h)' }}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="estimatedDeliveryAt">{t('funding.reward.estimatedDeliveryAt')}</Label>
        <Input
          id="estimatedDeliveryAt"
          type="date"
          {...form.register('estimatedDeliveryAt')}
          style={{ height: 'var(--density-compact-form-h)' }}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="sortOrder">{t('funding.reward.sortOrder')}</Label>
        <Input
          id="sortOrder"
          type="number"
          min={0}
          {...form.register('sortOrder', { valueAsNumber: true })}
          style={{ height: 'var(--density-compact-form-h)' }}
        />
      </div>

      {lockedDueToPledges ? (
        <p
          className="md:col-span-2 rounded-md p-2 text-xs"
          style={{
            background: 'var(--status-warn-bg)',
            color: 'var(--status-warn-fg)',
          }}
        >
          {t('funding.reward.lockedDueToPledges')}
        </p>
      ) : null}

      {error ? (
        <p
          role="alert"
          className="md:col-span-2 rounded-md p-2 text-sm"
          style={{
            background: 'var(--status-danger-bg)',
            color: 'var(--status-danger-fg)',
          }}
        >
          {error}
        </p>
      ) : null}

      <div className="md:col-span-2 flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          onClick={onDone}
          disabled={mutation.isPending}
        >
          {t('funding.reward.cancelAction')}
        </Button>
        <Button type="submit" variant="cta" disabled={mutation.isPending}>
          {t('funding.reward.saveAction')}
        </Button>
      </div>
    </form>
  );
};

interface RewardRowProps {
  reward: FundingReward;
  campaignId: string;
  funding: Funding;
  canWrite: boolean;
  canEdit: boolean;
  onEdit: () => void;
}

const RewardRow = ({ reward, campaignId, funding, canWrite, canEdit, onEdit }: RewardRowProps) => {
  const { t } = useTranslation('funding');
  const deleteMutation = useDeleteFundingRewardMutation();
  const error = errorMessageOf(deleteMutation.error);
  const max = reward.maxQuantity != null ? t('funding.reward.ofMax', { max: reward.maxQuantity }) : '';
  const hasPledges = reward.currentQuantity > 0;
  const isFinalState =
    funding.status === 'SUCCEEDED' ||
    funding.status === 'FAILED' ||
    funding.status === 'CANCELLED' ||
    funding.status === 'SETTLED';
  const canDelete = canWrite && !hasPledges && !isFinalState;
  // QA-398 — 리워드 상세(설명·발송일 등)를 펼쳐 볼 수 있게.
  const [open, setOpen] = useState(false);

  return (
    <li data-testid="reward-row" className="flex flex-col gap-2 rounded-md p-3" style={{ background: 'var(--surface-1)' }}>
      <div className="flex items-center justify-between gap-3">
      <div className="flex flex-col">
        <span className="text-sm font-medium text-[color:var(--content-strong)]">
          {reward.tierName}{' '}
          <span
            className="ml-1 inline-flex rounded px-1.5 py-0.5 text-[10px]"
            style={{ background: 'var(--surface-raised)', color: 'var(--content-muted)' }}
          >
            {t(`funding.reward.type_${reward.type}`, { defaultValue: '-' })}
          </span>
        </span>
        <span className="text-xs text-[color:var(--content-muted)]">
          {Intl.NumberFormat('ko-KR').format(reward.minPledgeAmount.amount)} KRW ·{' '}
          {t('funding.reward.currentQuantity', { current: reward.currentQuantity, max })}
        </span>
      </div>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          data-testid="reward-detail-toggle"
        >
          {open ? t('funding.reward.hideDetail', '상세 닫기') : t('funding.reward.showDetail', '상세 보기')}
        </Button>
      {canWrite ? (
        <>
          {canEdit ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label={t('funding.reward.editAria', { name: reward.tierName })}
              onClick={onEdit}
            >
              {/* QA-205②: 리워드 행 편집 버튼 라벨이 "저장"(actions.save)으로 오표기 →
                  실제 동작은 편집 폼 열기이므로 "수정"(actions.edit)으로 정정. */}
              {t('funding.actions.edit', { defaultValue: '수정' })}
            </Button>
          ) : null}
          {/* QA-398 — 삭제할 수 없는 상태(참여 있음·종료 상태)에서는 버튼을 숨긴다(종전엔 비활성으로 보였다). */}
          {canDelete ? (
            <Button
              type="button"
              variant="danger"
              size="sm"
              aria-label={t('funding.reward.removeAria', { name: reward.tierName })}
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate({ campaignId, rewardId: reward.rewardId })}
              data-testid="reward-delete"
            >
              {t('funding.reward.removeAction')}
            </Button>
          ) : null}
        </>
      ) : null}
      </div>
      </div>
      {open ? (
        <dl className="grid grid-cols-[120px_minmax(0,1fr)] gap-x-3 gap-y-1 rounded-md p-3 text-xs" style={{ background: 'var(--surface-raised)' }} data-testid="reward-detail">
          <dt className="text-[color:var(--content-muted)]">{t('funding.reward.tierName')}</dt>
          <dd className="text-[color:var(--content-strong)]">{reward.tierName}</dd>
          <dt className="text-[color:var(--content-muted)]">{t('funding.reward.type')}</dt>
          <dd className="text-[color:var(--content-strong)]">{t(`funding.reward.type_${reward.type}`, { defaultValue: '-' })}</dd>
          <dt className="text-[color:var(--content-muted)]">{t('funding.reward.minPledgeAmount')}</dt>
          <dd className="text-[color:var(--content-strong)]">{Intl.NumberFormat('ko-KR').format(reward.minPledgeAmount.amount)} KRW</dd>
          <dt className="text-[color:var(--content-muted)]">{t('funding.reward.maxQuantity')}</dt>
          <dd className="text-[color:var(--content-strong)]">{reward.maxQuantity != null ? reward.maxQuantity.toLocaleString('ko-KR') : t('funding.reward.remainingUnlimited')}</dd>
          <dt className="text-[color:var(--content-muted)]">{t('funding.reward.selectedLabel', '선택된 수량')}</dt>
          <dd className="text-[color:var(--content-strong)]">{reward.currentQuantity.toLocaleString('ko-KR')}</dd>
          <dt className="text-[color:var(--content-muted)]">{t('funding.reward.estimatedDeliveryAt')}</dt>
          <dd className="text-[color:var(--content-strong)]">{reward.estimatedDeliveryAt || '—'}</dd>
          <dt className="text-[color:var(--content-muted)]">{t('funding.reward.description')}</dt>
          <dd className="whitespace-pre-wrap text-[color:var(--content-strong)]">{reward.description || '—'}</dd>
        </dl>
      ) : null}
      {error ? (
        <span
          role="alert"
          className="text-xs"
          style={{ color: 'var(--status-danger-fg)' }}
        >
          {error}
        </span>
      ) : null}
    </li>
  );
};

export const RewardManager = ({ funding, canWrite }: RewardManagerProps) => {
  const { t } = useTranslation('funding');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const editTarget = funding.rewards.find((r) => r.rewardId === editingId);
  // BE 계약(be-settle-partner D-2): 리워드 추가·수정은 DRAFT 상태에서만 허용. 그 외엔 409.
  const isDraft = funding.status === 'DRAFT';
  const canAdd = canWrite && isDraft;

  return (
    <section
      aria-labelledby="reward-manager-heading"
      className="space-y-3"
      data-testid="reward-manager"
    >
      <header className="flex items-center justify-between">
        <h3
          id="reward-manager-heading"
          className="text-sm font-semibold text-[color:var(--content-strong)]"
        >
          {t('funding.reward.sectionTitle')}
        </h3>
        {canAdd ? (
          <Button
            type="button"
            variant="cta"
            size="sm"
            onClick={() => {
              setAdding(true);
              setEditingId(null);
            }}
            disabled={adding}
          >
            {t('funding.reward.addAction')}
          </Button>
        ) : null}
      </header>

      {canWrite && !isDraft ? (
        <p
          className="rounded-md p-2 text-xs"
          style={{ background: 'var(--status-warn-bg)', color: 'var(--status-warn-fg)' }}
        >
          {t('funding.reward.onlyDraft')}
        </p>
      ) : null}

      {adding ? (
        <RewardForm
          campaignId={funding.campaignId}
          defaultValues={emptyFundingRewardValues()}
          isEdit={false}
          onDone={() => setAdding(false)}
        />
      ) : null}

      {funding.rewards.length === 0 && !adding ? (
        <p className="rounded-md p-4 text-sm text-[color:var(--content-muted)]">
          {t('funding.reward.empty')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {funding.rewards.map((reward) => (
            <div key={reward.rewardId}>
              {editingId === reward.rewardId && editTarget ? (
                <RewardForm
                  campaignId={funding.campaignId}
                  defaultValues={toFormValues(editTarget)}
                  isEdit
                  editTarget={editTarget}
                  lockedDueToPledges={
                    funding.status === 'ACTIVE' && reward.currentQuantity > 0
                  }
                  onDone={() => setEditingId(null)}
                />
              ) : (
                <RewardRow
                  reward={reward}
                  campaignId={funding.campaignId}
                  funding={funding}
                  canWrite={canWrite}
                  canEdit={isDraft}
                  onEdit={() => {
                    setEditingId(reward.rewardId);
                    setAdding(false);
                  }}
                />
              )}
            </div>
          ))}
        </ul>
      )}
    </section>
  );
};
